import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { subYears } from "date-fns"
import { uniq } from "~/utils/tsUtils"
import { capitalize } from "lodash"
import { motion } from "motion/react"
import { type AgGridReact } from "ag-grid-react"
import {
  type ColDef,
  type RowSelectionOptions,
  type SelectionChangedEvent,
} from "ag-grid-community"

import { Button, IconButton } from "~/components/reusable/Button"
import { Tooltip } from "~/components/reusable/Tooltip"
import { type AggregatedAdjustment, convertAdjustmentValue } from "~/models/adjustment"
import { useAdjustments, useDeleteManyAdjustments } from "~/hooks/useAdjustments"
import { aggregateGlobal, buildSystemAdjustmentExplanation } from "~/utils/adjustmentsUtility"
import { api } from "~/utils/api"
import { DateFormatType, formatDate, formatMonthKeyRange, formatMonthKeys } from "~/utils/datetime"
import { startOfTodayUTC } from "~/utils/datetime"
import { useForecastUpdatesLink } from "~/hooks/useForecastUpdatesLink"
import { CellUser } from "~/components/reusable/CellUser"
import { formatNumber } from "~/utils/formatting"

import { ManageAdjustmentDialog } from "./ManageAdjustmentDialog"
import { DeleteAdjustmentDialog } from "./DeleteAdjustmentDialog"
import { JoinExistingAdjustmentDialog } from "./JoinExistingAdjustmentDialog"
import { ManageVariantsInAdjustmentDialog } from "./ManageVariantsInAdjustmentDialog"
import { type IVariantSummary } from "~/hooks/useVariantTableData"
import { AdjustmentLock } from "~/components/reusable/AdjustmentLock"
import { ForecastEditBadge } from "~/components/forecast/ForecastEditBadge"
import { VariantListDialog } from "~/components/reusable/VariantListDialog"

import ChannelsSummary from "~/components/reusable/ChannelsSummary"
import { Switch } from "~/components/reusable/Switch"
import { TabsList, TabsRoot, TabsTrigger } from "~/components/reusable/Tabs"
import { Input } from "~/components/reusable/Input"
import Select from "~/components/reusable/Select"
import { trackEvent } from "~/hooks/useTrackingService"
import { useUser } from "~/providers/userProvider"
import { DataTableBaseV2 } from "~/components/reusable/DataTableV2"
import {
  DialogContentV2,
  DialogFooterV2,
  DialogHeaderV2,
  DialogBodyV2,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "~/components/reusable/Dialog"
import ForecastManyGraph from "../ForecastManyGraph"
import {
  createStringColumn,
  createCustomColumn,
  createNumberColumn,
  createDateColumn,
  createActionsColumn,
  ActionsWrapperClassName,
} from "~/utils/columnTemplates"
import { ArrowSquareOut, LinkSimple, PencilSimple, TrashSimple } from "@phosphor-icons/react"
import { ShowHideContainer } from "~/components/reusable/ShowHideContainer"
import { CommonColumn } from "~/constants/commonColumns"
import { AdjustmentManagerColumn } from "~/components/planning/ProductTable/utils/columnUtils"

type AdjustmentRow = Omit<AggregatedAdjustment, "updatedAt"> & {
  id: string
  updatedAt: Date | null
  /** Synthetic row representing Forecast Updates edits, not a real adjustment. */
  isForecastEdit?: boolean
  /** "YYYY-MM" months covered by Forecast Updates edits (forecast-edit row only). */
  months?: string[]
  /** Total units the edits set the forecast to (forecast-edit row only). */
  targetTotal?: number
}

const FORECAST_EDIT_ROW_ID = "__forecast-edit__"

// The forecast-edit row can't be edited/deleted here, so any row an action was
// opened for is a real adjustment with a non-null updatedAt.
function isRealAdjustment(row: AdjustmentRow): row is AdjustmentRow & { updatedAt: Date } {
  return !row.isForecastEdit
}

type PastAdjustmentRow = {
  id: string
  name: string
  theme: string
  type: string
  value: number
  startDate: Date
  endDate: Date
  variantIds: string[]
}

type ActiveAction =
  | { type: "manageVariants"; row: AdjustmentRow }
  | { type: "edit"; row: AdjustmentRow }
  | { type: "delete"; row: AdjustmentRow }
  | { type: "bulkDelete"; rows: AdjustmentRow[] }
  | { type: "viewPastVariants"; row: PastAdjustmentRow }
  | null

const themeFilterOptions = [
  { value: "all", label: "All themes" },
  { value: "marketing", label: "Marketing" },
  { value: "strategy", label: "Strategy" },
  { value: "misc", label: "Misc" },
]

type Props = {
  variantIds: string[]
  allVariants?: IVariantSummary[]
  /** Trigger element. Omit when controlling the dialog via `open`/`onOpenChange`. */
  children?: ReactNode
  /** Controlled open state. When set, `onOpenChange` must also be provided. */
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

function getAdjustmentRowId(adj: AggregatedAdjustment): string {
  return `${adj.groupId}-${adj.warehouseId}`
}

/** The theme + name filters shared by both tabs' tables. */
function filterAdjustments<T extends { name: string; theme: string }>(
  rows: T[],
  theme: string | undefined,
  searchText: string,
): T[] {
  let result = theme && theme !== "all" ? rows.filter((adj) => adj.theme === theme) : rows
  const search = searchText.toLowerCase().trim()
  if (search) result = result.filter((adj) => adj.name.toLowerCase().includes(search))
  return result
}

export function AdjustmentManagerDialog({
  variantIds,
  allVariants,
  children,
  open: controlledOpen,
  onOpenChange,
}: Props) {
  const gridRef = useRef<AgGridReact<AdjustmentRow>>(null)
  const pastGridRef = useRef<AgGridReact<PastAdjustmentRow>>(null)
  const [internalOpen, setInternalOpen] = useState(false)
  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : internalOpen
  const setOpen = isControlled ? (onOpenChange ?? (() => {})) : setInternalOpen
  const [activeAction, setActiveAction] = useState<ActiveAction>(null)
  // Snapshot variantIds and allVariants when the dialog opens so mutations that
  // trigger cache refreshes (refreshVariants → rawVariants → allVariants) don't
  // change the context or cause re-renders inside sub-dialogs mid-session.
  const [frozenVariantIds, setFrozenVariantIds] = useState<string[]>(variantIds)
  const [frozenAllVariants, setFrozenAllVariants] = useState<IVariantSummary[]>(allVariants ?? [])

  const [selectedRows, setSelectedRows] = useState<AdjustmentRow[]>([])

  const { permissions } = useUser()
  const { data: warehouses } = api.warehouse.get.useQuery()

  const [showAll, setShowAll] = useState(false)
  const { data: variantAdjustments, isLoading: isLoadingVariant } = useAdjustments(frozenVariantIds)
  const { data: allAdjustments, isLoading: isLoadingAll } = useAdjustments(
    undefined,
    undefined,
    undefined,
    undefined,
    showAll,
  )
  const adjustments = showAll ? allAdjustments : variantAdjustments
  const isLoading = showAll ? isLoadingAll : isLoadingVariant

  const [searchText, setSearchText] = useState("")
  const [themeFilter, setThemeFilter] = useState(themeFilterOptions[0])

  const showWarehouse = warehouses && warehouses.length > 1

  const aggregated = useMemo(() => {
    if (!adjustments || !warehouses) return []
    return aggregateGlobal(adjustments, warehouses)
  }, [adjustments, warehouses])

  // Forecast Updates edits (MonthlyAdjust) shown as their own row so they are
  // clearly separated from manually created adjustments.
  const { data: monthlyAdjustSummary } = api.forecasts.getMonthlyAdjustSummary.useQuery(
    { variantIds: showAll ? undefined : frozenVariantIds },
    { enabled: open && (showAll || frozenVariantIds.length > 0) },
  )

  const forecastEditRow: AdjustmentRow | null = useMemo(() => {
    if (!monthlyAdjustSummary?.length) return null
    const startDate = new Date(Math.min(...monthlyAdjustSummary.map((s) => s.startDate.getTime())))
    const endDate = new Date(Math.max(...monthlyAdjustSummary.map((s) => s.endDate.getTime())))
    const warehouseIds = uniq(monthlyAdjustSummary.flatMap((s) => s.warehouseIds))
    return {
      id: FORECAST_EDIT_ROW_ID,
      isForecastEdit: true,
      name: "Forecast edits",
      groupId: FORECAST_EDIT_ROW_ID,
      variantIds: monthlyAdjustSummary.map((s) => s.variantId),
      value: 0,
      type: "custom",
      theme: "",
      warehouseId: warehouseIds.length === 1 ? warehouseIds[0]! : "",
      channels: uniq(monthlyAdjustSummary.flatMap((s) => s.channels)),
      startDate,
      endDate,
      updatedBy: "",
      updatedAt: null,
      locksForecast: true,
      identifiers: [],
      referencedAdjustments: [],
      months: uniq(monthlyAdjustSummary.flatMap((s) => s.months)),
      targetTotal: monthlyAdjustSummary.reduce((sum, s) => sum + Number(s.targetTotal), 0),
    }
  }, [monthlyAdjustSummary])

  // Where the edits can be viewed/changed. Enabled while the row is visible so
  // the "Open Forecast Updates" action is ready when clicked.
  const { canOpen: canOpenForecastUpdates, openForecastUpdates } = useForecastUpdatesLink(
    forecastEditRow?.variantIds ?? [],
    open && forecastEditRow !== null,
  )

  const rowData: AdjustmentRow[] = useMemo(
    () =>
      filterAdjustments(aggregated, themeFilter?.value, searchText).map((adj) => ({
        ...adj,
        id: getAdjustmentRowId(adj),
      })),
    [aggregated, themeFilter, searchText],
  )

  const [activeTab, setActiveTab] = useState<"active" | "past">("active")

  // Past = concluded adjustments, incl. ones archived to AdjustmentVariantHistoric by
  // deletes/demand-plan publishes. These back the flagged periods on the variant graph
  // (REKD-674) and appear nowhere else in the UI. Read-only: there is nothing to edit.
  const { data: pastOverlapRows, isLoading: isLoadingPast } =
    api.adjustment.getOverlappingForVariantsInRange.useQuery(
      {
        variantIds: frozenVariantIds,
        startDate: subYears(startOfTodayUTC(), 2),
        endDate: startOfTodayUTC(),
        includeHistoric: true,
      },
      { enabled: open && activeTab === "past" && frozenVariantIds.length > 0 },
    )

  // Split from the filtering below (as `aggregated` is on the Active tab) so typing in the
  // search box doesn't redo the grouping — and so each row's `variantIds` keeps its identity.
  const pastAggregated = useMemo<PastAdjustmentRow[]>(() => {
    const today = startOfTodayUTC()
    const byKey = new Map<string, PastAdjustmentRow>()
    for (const row of pastOverlapRows ?? []) {
      for (const a of row.adjustments) {
        if (a.isSystem || a.endDate >= today) continue
        const key = `${a.groupId}|${a.endDate.getTime()}`
        const existing = byKey.get(key)
        if (existing) {
          existing.variantIds.push(row.variantId)
          // Unit-based values are per variant; the total across variants is what the
          // Active tab's aggregation shows too. Percentages are identical per variant.
          if (a.type === "add") existing.value += a.value
        } else {
          byKey.set(key, { ...a, id: key, variantIds: [row.variantId] })
        }
      }
    }
    return [...byKey.values()].sort((a, b) => b.endDate.getTime() - a.endDate.getTime())
  }, [pastOverlapRows])

  const pastRowData = useMemo(
    () => filterAdjustments(pastAggregated, themeFilter?.value, searchText),
    [pastAggregated, themeFilter, searchText],
  )

  const pastColDefs = useMemo<ColDef<PastAdjustmentRow>[]>(
    () => [
      createStringColumn<PastAdjustmentRow>({
        field: "name",
        headerName: "Name",
        flex: 1,
        minWidth: 180,
      }),
      createStringColumn<PastAdjustmentRow>({
        colId: AdjustmentManagerColumn.Theme,
        field: "theme",
        initialWidth: 90,
        valueFormatter: (params) => (params.value ? capitalize(params.value) : ""),
      }),
      createCustomColumn<PastAdjustmentRow>({
        colId: AdjustmentManagerColumn.Value,
        initialWidth: 80,
        valueGetter: (params) => params.data?.value ?? 0,
        valueFormatter: (params) =>
          params.data ? String(convertAdjustmentValue(params.data.value, params.data.type)) : "",
      }),
      createCustomColumn<PastAdjustmentRow>({
        colId: AdjustmentManagerColumn.Period,
        initialWidth: 200,
        valueGetter: (params) =>
          params.data
            ? `${formatDate(params.data.startDate)} - ${formatDate(params.data.endDate)}`
            : "",
      }),
      createNumberColumn<PastAdjustmentRow>({
        colId: AdjustmentManagerColumn.VariantCount,
        initialWidth: 80,
        valueGetter: (params) => params.data?.variantIds.length ?? 0,
        onCellClicked: (params) => {
          const adj = params.data as PastAdjustmentRow
          if (!adj) return
          setActiveAction({ type: "viewPastVariants", row: adj })
        },
      }),
    ],
    [],
  )

  // Forecast Updates edits render as a pinned top row: always visible, never
  // sorted or selected among the adjustments, visually a different kind of thing.
  const pinnedTopRowData = useMemo(
    () => (forecastEditRow ? [forecastEditRow] : []),
    [forecastEditRow],
  )

  const allCount = aggregated.length + (forecastEditRow ? 1 : 0)

  const colDefs = useMemo(() => {
    const nameCol = createStringColumn<AdjustmentRow>({
      field: "name",
      headerName: "Name",
      flex: 1,
      minWidth: 180,
      cellRenderer: (params: any) => {
        const adj = params.data as AdjustmentRow
        if (!adj) return null
        return (
          <div className="flex items-center gap-2 h-[38px]">
            <AdjustmentLock locksForecast={adj.locksForecast} size={4} />
            <span className="label-base">{adj.name}</span>
            {adj.isForecastEdit && <ForecastEditBadge />}
          </div>
        )
      },
    })

    const themeCol = createStringColumn<AdjustmentRow>({
      colId: AdjustmentManagerColumn.Theme,
      field: "theme",
      initialWidth: 90,
      valueFormatter: (params) => (params.value ? capitalize(params.value) : ""),
    })

    const warehouseCol = createStringColumn<AdjustmentRow>({
      colId: AdjustmentManagerColumn.Warehouse,
      initialWidth: 90,
      valueGetter: (params) => {
        const wId = params.data?.warehouseId
        return warehouses?.find((w) => w.id === wId)?.name ?? wId ?? ""
      },
    })

    const channelsCol = createCustomColumn<AdjustmentRow>({
      colId: AdjustmentManagerColumn.Channels,
      initialWidth: 90,
      sortable: false,
      cellRenderer: (params: any) => {
        if (!params.data) return null
        return <ChannelsSummary channelIds={params.data.channels} />
      },
    })

    const valueCol = createCustomColumn<AdjustmentRow>({
      colId: AdjustmentManagerColumn.Value,
      initialWidth: 80,
      valueGetter: (params) => params.data?.value ?? 0,
      cellRenderer: (params: any) => {
        const adj = params.data as AdjustmentRow
        if (!adj) return null
        if (adj.isForecastEdit) {
          return (
            <Tooltip
              title={`The forecast for the edited months is set to ${formatNumber(adj.targetTotal ?? 0, 0)} units in total (across ${adj.variantIds.length} variant${adj.variantIds.length !== 1 ? "s" : ""}), replacing what the system forecasted.`}
            >
              <span className="text-fg-subtle">= {formatNumber(adj.targetTotal ?? 0, 0)}</span>
            </Tooltip>
          )
        }
        const text = convertAdjustmentValue(adj.value, adj.type)
        const colorClass = adj.value > 0 ? "text-green-600" : adj.value < 0 ? "text-red-500" : ""
        return <span className={colorClass}>{text}</span>
      },
    })

    const variantsCol = createNumberColumn<AdjustmentRow>({
      colId: AdjustmentManagerColumn.VariantCount,
      initialWidth: 80,
      valueGetter: (params) => params.data?.variantIds.length ?? 0,
      onCellClicked: (params) => {
        const adj = params.data as AdjustmentRow
        if (!adj || adj.isForecastEdit || showAll || !permissions.canEdit) return
        setActiveAction({ type: "manageVariants", row: adj })
      },
    })

    const periodCol = createCustomColumn<AdjustmentRow>({
      colId: AdjustmentManagerColumn.Period,
      initialWidth: 200,
      valueGetter: (params) =>
        params.data
          ? `${formatDate(params.data.startDate)} - ${formatDate(params.data.endDate)}`
          : "",
      cellRenderer: (params: any) => {
        const adj = params.data as AdjustmentRow
        if (!adj) return null
        // A min-max range over per-variant edits can span months nobody touched;
        // show the edited months instead and list them exactly in the tooltip.
        if (adj.isForecastEdit && adj.months?.length) {
          return (
            <Tooltip title={`Edited months: ${formatMonthKeys(adj.months)}`}>
              <span>{formatMonthKeyRange(adj.months)}</span>
            </Tooltip>
          )
        }
        return <span>{params.value}</span>
      },
    })

    const updatedByCol = createCustomColumn<AdjustmentRow>({
      colId: AdjustmentManagerColumn.UpdatedBy,
      initialWidth: 150,
      valueGetter: (params) => params.data?.updatedBy ?? "",
      cellRenderer: (params: any) => {
        if (!params.data?.updatedBy) return null
        if (params.data.updatedBy === "system") {
          const explanation = buildSystemAdjustmentExplanation({
            name: params.data.name,
            startDate: params.data.startDate,
            endDate: params.data.endDate,
            channels: params.data.channels,
            variantCount: params.data.variantIds.length,
          })
          return (
            <Tooltip
              title={explanation ?? "This adjustment was automatically created by the system"}
            >
              <span className="paragraph-xs text-fg-muted">System</span>
            </Tooltip>
          )
        }
        return <CellUser userId={params.data.updatedBy} />
      },
    })

    const updatedAtCol = createDateColumn<AdjustmentRow>({
      field: "updatedAt",
      headerName: "Updated at",
      dateFormat: DateFormatType.WithTime,
      initialWidth: 180,
    })

    const actionsCol = createActionsColumn<AdjustmentRow>({
      colId: CommonColumn.Actions,
      numberOfActions: 3,
      cellRenderer: (params: any) => {
        const adj = params.data as AdjustmentRow

        if (!adj) return null

        if (adj.isForecastEdit) {
          return (
            <div className={ActionsWrapperClassName}>
              <Tooltip
                title={
                  canOpenForecastUpdates
                    ? "Open Forecast Updates to view or change the edited forecasts"
                    : "These edits are made on the Forecast Updates page of the variants' collection"
                }
              >
                <span className="inline-flex">
                  <IconButton
                    variant="neutral"
                    size="sm"
                    disabled={!canOpenForecastUpdates}
                    onClick={() => {
                      setOpen(false)
                      openForecastUpdates()
                    }}
                  >
                    <ArrowSquareOut />
                  </IconButton>
                </span>
              </Tooltip>
            </div>
          )
        }

        return (
          <div className={ActionsWrapperClassName}>
            <IconButton
              requires="edit"
              allowedTitle={
                showAll
                  ? "Switch to selected variants to manage variants"
                  : "Manage variants in this adjustment"
              }
              variant="neutral"
              size="sm"
              disabled={showAll}
              onClick={() => setActiveAction({ type: "manageVariants", row: adj })}
            >
              <LinkSimple />
            </IconButton>
            <IconButton
              requires="edit"
              allowedTitle="Edit adjustment"
              variant="neutral"
              size="sm"
              onClick={() => setActiveAction({ type: "edit", row: adj })}
            >
              <PencilSimple />
            </IconButton>
            <IconButton
              requires="edit"
              allowedTitle="Delete adjustment for all variants"
              variant="danger"
              size="sm"
              onClick={() => setActiveAction({ type: "delete", row: adj })}
            >
              <TrashSimple />
            </IconButton>
          </div>
        )
      },
    })

    const cols: ColDef<AdjustmentRow>[] = [
      nameCol,
      themeCol,
      ...(showWarehouse ? [warehouseCol] : []),
      channelsCol,
      valueCol,
      periodCol,
      updatedByCol,
      updatedAtCol,
      variantsCol,
      actionsCol,
    ]

    return cols
  }, [
    showWarehouse,
    warehouses,
    showAll,
    permissions,
    setActiveAction,
    canOpenForecastUpdates,
    openForecastUpdates,
    setOpen,
  ])

  const defaultColDef = useMemo<ColDef>(
    () => ({
      minWidth: 60,
      sortable: true,
      suppressHeaderMenuButton: true,
    }),
    [],
  )

  const rowSelection = useMemo<RowSelectionOptions<AdjustmentRow>>(
    () => ({ mode: "multiRow", selectAll: "filtered" }),
    [],
  )

  const handleSelectionChanged = useCallback((event: SelectionChangedEvent) => {
    const nodes = event.api.getSelectedNodes()
    setSelectedRows(nodes.map((n) => n.data as AdjustmentRow))
  }, [])

  // Active-tab rows only: `viewPastVariants` carries a past row, which `rowData` never holds.
  const liveActionRow =
    activeAction &&
    (activeAction.type === "manageVariants" ||
      activeAction.type === "edit" ||
      activeAction.type === "delete")
      ? (rowData.find((r) => r.id === activeAction.row.id) ?? activeAction.row)
      : null

  // Run open/close side effects on the `open` transition itself (not in onOpenChange)
  // so they also fire when the dialog is controlled by the parent. The snapshot happens
  // during render, before sub-components query with the frozen ids.
  const [prevOpen, setPrevOpen] = useState(open)
  if (open !== prevOpen) {
    setPrevOpen(open)
    if (open) {
      setFrozenVariantIds(variantIds)
      setFrozenAllVariants(allVariants ?? [])
    } else {
      setSelectedRows([])
      setActiveTab("active")
    }
  }

  useEffect(() => {
    if (open) trackEvent("Adjustment All Dialog Opened")
  }, [open])

  return (
    <DialogRoot open={open} onOpenChange={setOpen}>
      {children && <DialogTrigger onClick={(e) => e.stopPropagation()}>{children}</DialogTrigger>}
      <DialogContentV2 className="max-w-[85%] max-h-[90vh]">
        <DialogHeaderV2>
          <DialogTitle>
            Adjustments
            {!showAll && frozenVariantIds.length > 0 && (
              <span className="ml-2 text-sm font-normal text-fg-subtle">
                {formatNumber(frozenVariantIds.length, 0)} variants
              </span>
            )}
          </DialogTitle>
        </DialogHeaderV2>

        <DialogBodyV2 className="flex flex-col gap-4 overflow-y-auto">
          <div className="flex items-center gap-3">
            <TabsRoot value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)}>
              <TabsList>
                <TabsTrigger value="active">Active</TabsTrigger>
                <TabsTrigger value="past">Historic</TabsTrigger>
              </TabsList>
            </TabsRoot>
            <Input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search adjustments..."
              containerClassName="w-[240px]"
            />
            <Select
              value={themeFilter}
              onChange={(val: any) => setThemeFilter(val)}
              options={themeFilterOptions}
              className="w-[160px]"
            />
            <div className="ml-auto flex items-center gap-3">
              {activeTab === "active" && (
                <label className="flex items-center gap-2 text-sm text-fg-subtle cursor-pointer">
                  <Switch size="xsmall" checked={showAll} onCheckedChange={setShowAll} />
                  Show all variants
                </label>
              )}
              <span className="text-sm text-fg-subtle">
                {activeTab === "past" ? pastRowData.length : rowData.length} adjustment
                {(activeTab === "past" ? pastRowData.length : rowData.length) !== 1 ? "s" : ""}
              </span>
            </div>
          </div>

          {/* display:none instead of unmount/height-animation: the chart stays mounted (no
              re-render or refetch when returning to Active) and the dialog height changes
              once per tab switch instead of contracting and growing back. The showAll
              collapse within the Active tab still animates. */}
          <div className={activeTab === "past" ? "hidden" : undefined}>
            <ShowHideContainer isVisible={!showAll}>
              <ForecastManyGraph
                variantIds={frozenVariantIds}
                allowAdjustments
                height={300}
                firstDisplayDay={subYears(startOfTodayUTC(), 1)}
              />
            </ShowHideContainer>
          </div>

          {/* Keyed on the tab so the incoming table fades in. */}
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
          >
            {activeTab === "past" ? (
              <DataTableBaseV2<PastAdjustmentRow>
                id="adjustment-manager-past"
                gridRef={pastGridRef}
                gridOptions={{ getRowId: (p) => p.data.id }}
                rowData={pastRowData}
                columnDefs={pastColDefs}
                defaultColumnDef={defaultColDef}
                loading={isLoadingPast}
                minHeight={500}
                emptyMessage="No historic adjustments in the last 2 years"
              />
            ) : allCount === 0 && !isLoading ? (
              <div className="my-16 text-center text-sm text-fg-disabled">
                No adjustments for the selected variants
              </div>
            ) : (
              <DataTableBaseV2<AdjustmentRow>
                id="adjustment-manager"
                gridRef={gridRef}
                gridOptions={{
                  getRowId: (p) => p.data.id,
                  rowSelection: !showAll ? rowSelection : undefined,
                  pinnedTopRowData,
                }}
                rowData={rowData}
                columnDefs={colDefs}
                defaultColumnDef={defaultColDef}
                onSelectionChanged={!showAll ? handleSelectionChanged : undefined}
                loading={isLoading}
                minHeight={500}
                emptyMessage="No adjustments match your filters"
              />
            )}
          </motion.div>
        </DialogBodyV2>

        {activeAction?.type === "manageVariants" &&
          liveActionRow &&
          isRealAdjustment(liveActionRow) && (
            <ManageVariantsInAdjustmentDialog
              adjustment={liveActionRow}
              availableVariants={frozenAllVariants}
              open
              onClose={() => setActiveAction(null)}
            />
          )}
        {activeAction?.type === "edit" && isRealAdjustment(activeAction.row) && (
          <ManageAdjustmentDialog
            adjustment={activeAction.row}
            variantIds={frozenVariantIds}
            customTrigger={<div />}
            customOpen
            onClose={() => setActiveAction(null)}
          />
        )}
        {activeAction?.type === "delete" && (
          <DeleteAdjustmentDialog
            name={activeAction.row.name}
            groupId={activeAction.row.groupId}
            identifiers={activeAction.row.identifiers}
            open
            onClose={() => setActiveAction(null)}
          />
        )}
        {activeAction?.type === "bulkDelete" && (
          <BulkDeleteAdjustmentDialog
            rows={activeAction.rows}
            open
            onClose={() => setActiveAction(null)}
          />
        )}
        {/* Past rows live in their own table, so read the row directly rather than `liveActionRow`. */}
        {activeAction?.type === "viewPastVariants" && (
          <VariantListDialog
            variantIds={activeAction.row.variantIds}
            open
            onClose={() => setActiveAction(null)}
          />
        )}

        <DialogFooterV2 className="flex justify-end gap-3">
          {activeTab === "past" ? (
            <span className="text-xs text-fg-disabled mr-auto">
              Historic adjustments are read-only and no longer affect the forecast
            </span>
          ) : showAll ? (
            <span className="text-xs text-fg-disabled mr-auto">
              Switch to selected variants to manage adjustments
            </span>
          ) : (
            <>
              <Button
                requires="edit"
                disabled={selectedRows.length === 0}
                onClick={() => setActiveAction({ type: "bulkDelete", rows: selectedRows })}
                variant="danger"
              >
                <TrashSimple />
                Delete {selectedRows.length} adjustment{selectedRows.length !== 1 ? "s" : ""}
              </Button>
              <JoinExistingAdjustmentDialog variantIds={frozenVariantIds} />
              <ManageAdjustmentDialog
                variantIds={frozenVariantIds}
                customTrigger={<Button>New Adjustment</Button>}
              />
            </>
          )}
        </DialogFooterV2>
      </DialogContentV2>
    </DialogRoot>
  )
}

function BulkDeleteAdjustmentDialog({
  rows,
  open,
  onClose,
}: {
  rows: AdjustmentRow[]
  open: boolean
  onClose: () => void
}) {
  const deleteMany = useDeleteManyAdjustments()
  const totalVariants = new Set(rows.flatMap((r) => r.variantIds)).size

  const handleDelete = () => {
    deleteMany.mutate(
      {
        adjustments: rows.map((r) => ({
          groupId: r.groupId,
          warehouseIds: r.identifiers.map((id) => id.warehouseId),
          variantIds: r.identifiers.flatMap((id) => id.variantIds),
        })),
      },
      { onSuccess: onClose },
    )
  }

  return (
    <DialogRoot open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContentV2>
        <DialogHeaderV2>
          <DialogTitle>
            Delete {rows.length} adjustment{rows.length !== 1 ? "s" : ""}?
          </DialogTitle>
        </DialogHeaderV2>
        <DialogBodyV2 className="text-sm">
          <p>
            This will permanently delete the selected adjustments for{" "}
            <span className="font-medium">{totalVariants} variants</span>
          </p>
          <p className="mt-2 text-fg-subtle">
            This cannot be undone and will affect your forecast.
          </p>
        </DialogBodyV2>
        <DialogFooterV2 className="flex justify-between">
          <Button variant="transparent" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="danger"
            requires="edit"
            loading={deleteMany.isPending}
            onClick={handleDelete}
          >
            Delete {rows.length} adjustment{rows.length !== 1 ? "s" : ""}
          </Button>
        </DialogFooterV2>
      </DialogContentV2>
    </DialogRoot>
  )
}
